import React, {useCallback, useEffect, useState} from 'react';
import {View, ScrollView, StyleSheet, RefreshControl, TouchableOpacity, Switch} from 'react-native';
import {
  Text,
  ActivityIndicator,
  Snackbar,
  Dialog,
  Portal,
  Button,
  TextInput,
} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {workerTypeApi} from '../../services/api/workerTypeApi';
import {colors, spacing} from '../../theme';

const WorkerTypeListScreen = () => {
  const {t} = useTranslation();

  const [workerTypes, setWorkerTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  // Edit dialog state
  const [togglingId, setTogglingId] = useState(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [displayName, setDisplayName] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const response = await workerTypeApi.listWorkerTypes();
      setWorkerTypes(response.data.data);
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [t]);

  useEffect(() => {
    setIsLoading(true);
    fetchData().finally(() => setIsLoading(false));
  }, [fetchData]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  }, [fetchData]);

  const handleToggle = useCallback(async (item, newValue) => {
    setTogglingId(item.id);
    try {
      await workerTypeApi.updateSetting(item.id, {isEnabled: newValue});
      await fetchData();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    } finally {
      setTogglingId(null);
    }
  }, [fetchData, t]);

  const openEditDialog = useCallback((item) => {
    setEditingItem(item);
    setDisplayName(item.displayNameOverride || item.name);
    setDialogVisible(true);
  }, []);

  const handleSaveOverride = useCallback(async () => {
    if (!editingItem) return;
    try {
      await workerTypeApi.updateSetting(editingItem.id, {
        displayNameOverride: displayName.trim() || null,
      });
      setDialogVisible(false);
      setEditingItem(null);
      await fetchData();
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    }
  }, [editingItem, displayName, fetchData, t]);

  if (isLoading && workerTypes.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary]} />
        }>
        {workerTypes.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Icon name="briefcase-outline" size={48} color={colors.textLight} />
            <Text style={styles.emptyText}>{t('workerType.noData')}</Text>
          </View>
        ) : (
          workerTypes.slice().sort((a, b) => (b.isEnabled ? 1 : 0) - (a.isEnabled ? 1 : 0)).map(wt => (
            <TouchableOpacity
              key={wt.id}
              activeOpacity={0.7}
              style={[styles.row, !wt.isEnabled && styles.rowDisabled]}
              onPress={() => openEditDialog(wt)}>
              <View style={[styles.iconCircle, {backgroundColor: wt.isEnabled ? colors.primary + '15' : colors.border}]}>
                <Icon name="briefcase-outline" size={20} color={wt.isEnabled ? colors.primary : colors.textSecondary} />
              </View>
              <View style={styles.rowText}>
                <Text style={[styles.name, !wt.isEnabled && styles.textDisabled]}>
                  {wt.displayNameOverride || wt.name}
                </Text>
                {wt.description ? (
                  <Text style={styles.description} numberOfLines={1}>{wt.description}</Text>
                ) : null}
              </View>
              {togglingId === wt.id ? (
                <ActivityIndicator size={20} color={colors.primary} style={{marginRight: 6}} />
              ) : (
                <Switch
                  value={wt.isEnabled}
                  onValueChange={(val) => handleToggle(wt, val)}
                  trackColor={{false: colors.border, true: colors.primary + '60'}}
                  thumbColor={wt.isEnabled ? colors.primary : colors.textLight}
                />
              )}
            </TouchableOpacity>
          ))
        )}
      </ScrollView>

      {/* Edit Override Dialog */}
      <Portal>
        <Dialog
          visible={dialogVisible}
          onDismiss={() => setDialogVisible(false)}
          style={{borderRadius: 15}}>
          <Dialog.Title>{t('workerType.editOverride')}</Dialog.Title>
          <Dialog.Content>
            <TextInput
              label={t('workerType.displayName')}
              value={displayName}
              onChangeText={setDisplayName}
              mode="outlined"
              style={styles.dialogInput}
            />
            <TextInput
              label={t('workerType.code')}
              value={editingItem?.code || ''}
              mode="outlined"
              style={styles.dialogInput}
              disabled
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDialogVisible(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={handleSaveOverride}
              style={styles.saveButton}>
              {t('common.save')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  scrollContent: {paddingVertical: spacing.sm},

  // Empty state
  emptyContainer: {
    alignItems: 'center',
    paddingTop: spacing.xxl * 2,
  },
  emptyText: {
    color: colors.textSecondary,
    marginTop: spacing.sm,
    fontSize: 14,
  },

  // Row items
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    marginHorizontal: spacing.md,
    marginBottom: spacing.sm,
    padding: spacing.md,
    borderRadius: 10,
    gap: spacing.sm,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  rowDisabled: {
    opacity: 0.55,
  },
  iconCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  rowText: {flex: 1},
  name: {fontSize: 15, fontWeight: '600', color: colors.text},
  textDisabled: {color: colors.textSecondary},
  description: {fontSize: 12, color: colors.textSecondary, marginTop: 2},

  // Dialog
  dialogInput: {
    marginBottom: spacing.sm,
    backgroundColor: colors.surface,
  },
  saveButton: {
    backgroundColor: colors.primary,
    borderRadius: 10,
    paddingHorizontal: 10,
  },
});

export default WorkerTypeListScreen;
